// Wire + content types. Keys match server/prompts.mjs and the shared content.json
// (the same contract the iOS and Android apps use).

export type Level = "a0" | "a1" | "a2" | "b1" | "b2";
export type LearnerType = "new" | "heritage";
export type Register = "colloquial" | "formal" | "both";
export type Dialect = "iranian" | "dari" | "tajik";

export interface LearnerProfile {
  name: string;
  level: Level;
  learnerType: LearnerType;
  register: Register;
  dialect: Dialect;
  nativeLanguage: string;
  goals: string[];
  showFinglish: boolean;
  showEnglish: boolean;
  voiceSpeed: number;
  onboarded: boolean;
}

export const LEVELS: { id: Level; title: string; fa: string }[] = [
  { id: "a0", title: "Brand new", fa: "تازه‌کار" },
  { id: "a1", title: "I know a few words", fa: "مبتدی" },
  { id: "a2", title: "Simple conversations", fa: "پایه" },
  { id: "b1", title: "Pretty comfortable", fa: "متوسط" },
  { id: "b2", title: "Advanced", fa: "پیشرفته" },
];

export const REGISTERS: { id: Register; title: string; subtitle: string }[] = [
  { id: "colloquial", title: "How people really talk", subtitle: "Spoken Tehrani: mikhām, khune, miram" },
  { id: "formal", title: "Formal / written", subtitle: "Book Persian: mikhāham, khāne, miravam" },
  { id: "both", title: "Show me both", subtitle: "Colloquial first, with the written form alongside" },
];

export const DIALECTS: { id: Dialect; title: string }[] = [
  { id: "iranian", title: "Iranian Persian (Farsi)" },
  { id: "dari", title: "Dari (Afghanistan)" },
  { id: "tajik", title: "Tajik (Tajikistan)" },
];

export const DEFAULT_PROFILE: LearnerProfile = {
  name: "",
  level: "a1",
  learnerType: "new",
  register: "colloquial",
  dialect: "iranian",
  nativeLanguage: "English",
  goals: [],
  showFinglish: true,
  showEnglish: true,
  voiceSpeed: 0.9,
  onboarded: false,
};

// ---- server responses ----

export interface Line { fa: string; finglish: string; en: string }
export interface Correction { kind?: string | null; original: string; corrected: string; explanation: string }
export interface Vocab { fa: string; finglish: string; en: string; note?: string | null }
export interface ChatReply {
  reply: Line;
  counterpart?: { register: string; fa: string; finglish: string } | null;
  corrections: Correction[];
  vocab: Vocab[];
  culture_note?: string | null;
}
export interface Annotation { finglish: string; en: string; corrected?: string | null; explanation?: string | null }
export interface Breakdown { chunks: { fa: string; finglish: string; gloss: string; grammar?: string | null }[]; translation: string; takeaway: string }
export interface PronounceResult {
  heard: string;
  score: number;
  heard_finglish: string;
  feedback: string;
  words: { target: string; heard?: string | null; ok: boolean; tip?: string | null }[];
}
export interface RealtimeTicket { maxSeconds?: number; key: string; expiresAt?: number; model: string; url: string; callsUrl?: string }
export interface Story {
  theme: string;
  title_fa: string;
  title_en: string;
  lines: { speaker: string; fa: string; finglish: string; en: string }[];
  new_words: Line[];
  question: { q: string; options: string[]; answer: number };
  cast?: Record<string, string>;
}
export interface Gloss { fa: string; finglish: string; en: string; note?: string | null }

export type Feature = "voice" | "chat" | "story" | "pronounce";
export interface UsageEntry { used: number; limit: number; remaining: number }
export type Usage = Record<Feature, UsageEntry>;

export interface Subscription { status: string; plan: "yearly" | "monthly" | null; periodEnd: number | null; cancelAtPeriodEnd: boolean; trialEnd: number | null }
export interface User { id: string; email: string; createdAt: string; pro: boolean; trialUsed: boolean; subscription: Subscription | null }

// ---- content.json ----

export interface PathItem { fa: string; finglish: string; en: string; icon: string; note?: string | null }
export interface PathLesson { id: string; title: string; titleFa: string; items: PathItem[]; tip?: string | null }
/** `level` (1 = free Beginner; 2+ = Pro) and `scenario` (a role-play id for the unit-end call) are
 *  missing from older content.json: treat as level 1, no scenario. */
export interface PathUnit { id: string; title: string; titleFa: string; lessons: PathLesson[]; level?: number; scenario?: string | null }
export interface PersianLetter {
  char: string; name: string; sound: string; example: string; exampleFinglish: string; exampleEn: string;
  note?: string | null; nameFa: string; nonConnector: boolean;
}
export interface PracticePhrase { fa: string; finglish: string; en: string; focus: string }
export interface PhraseGroup { title: string; phrases: PracticePhrase[] }
export interface Scenario { id: string; icon: string; title: string; titleFa: string; brief: string; culture?: string | null; minLevel: string; opener?: string | null }
export interface ShopItem { id: string; slot: "hat" | "face" | "neck" | "held" | "halo"; name: string; fa: string; finglish: string; price: number; rarity: string; set?: string | null; blurb: string }
export interface CollectionSet { id: string; name: string; fa: string; bonus: number }
export interface HafezLine { fa: string; en: string }
export interface ContentBundle {
  version: number;
  units: PathUnit[];
  letters: PersianLetter[];
  phraseGroups: PhraseGroup[];
  scenarios: Scenario[];
  shop: ShopItem[];
  sets: CollectionSet[];
  hafez: HafezLine[];
  goals: string[];
}
